<template>
  <Teleport :to="teleport">
    <!-- 遮罩层 -->
    <Overlay
      v-if="overlay"
      :visible="visible"
      :overlay-class="overlayClass"
      :z-index="normalizedZIndex"
      :duration="duration"
      :lock-scroll="lockScroll"
      @click="onClickOverlay"
    />
    <!-- 弹窗内容 -->
    <Transition
      :name="n(`--pop-${position}`)"
      :duration="animationDuration"
      @after-enter="onOpened"
      @after-leave="onClosed"
    >
      <div
        v-show="visible"
        :class="[classes(n(), n('$--box'), n(`--${position}`), [position === 'center' && isLiteapp, n('--center-liteapp')]), popAttrs.class]"
        :style="{
          ...popStyle,
          ...popAttrs.style,
        }"
        role="dialog"
        aria-modal="true"
        v-bind="$attrs"
      >
        <!--
          center 定位：
          - H5（Vue2/2.7/3）：外层 .popup 直接用 transform 居中（原生行为，遮罩点击/按钮点击均正常）；
          - LiteApp：transform 会导致点击 hit-testing 错位，改用外层 .popup--center-liteapp 全屏 flex 容器居中，
            并用内层 .popup__center-body 作为真正的内容盒子承载背景/滚动。
          其余定位（top/bottom）保持原结构不变。
        -->
        <template v-if="position === 'center' && isLiteapp">
          <div :class="n('center-body')">
            <slot v-if="showSlot" />
          </div>
        </template>
        <template v-else>
          <slot v-if="showSlot" />
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import {
  ref,
  computed,
  defineComponent,
  watch,
  getCurrentInstance,
  isVue2,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import Overlay from '../overlay/overlay.vue';
import { platform } from '@tencent/wphk-platform-adapter';
import { createNamespace } from '../utils/components';
import { createZIndexManager, zIndexDefault } from '../use/useZIndex';

const { name, n, classes } = createNamespace('popup');

const initIndex = createZIndexManager('popup', zIndexDefault.popup);

export default defineComponent({
  name,
  components: { Overlay },
  inheritAttrs: false, // 禁止透传到根节点
  props,
  emits,
  setup(props, { emit }) {
    // 是否为 LiteApp 环境（center 定位在 LiteApp 需用全屏 flex 容器居中）
    const isLiteapp = platform.isLiteapp();
    // 弹窗状态
    let opened: boolean;
    // 是否展示slot
    const showSlot = ref(true);
    // 获取z-index管理
    const zIndex = ref(initIndex.next());
    const normalizedZIndex = computed(() => props.zIndex ?? zIndex.value);
    const animationDuration = computed(() => +props.duration * 1000);

    /**
     * Vue2 中会将 class 和 style 视为特殊属性，不会将它们放入 $attrs 中
     * 即使使用动态绑定:class，Vue 2 也会将其处理为 class 或 staticClass，而非 attrs
     * 因此在 Vue2中 使用v-bind="$attrs"也不会生效，需要手动绑定属性值
     */
    const popAttrs = ref({
      class: '',
      style: {},
    });

    // 弹窗样式
    const popStyle = computed(() => ({
      zIndex: normalizedZIndex.value,
      transitionDuration: `${props.duration}s`,
    }));

    /**
     * 在 Vue2 Teleport 组件的 parentData 中，class 是单独存在的属性，而不是在 attrs 中
     * 这是 Vue 2 的默认行为，无法通过简单配置改变
     * 只能手动获取组件的 class 和 style ， 进行合并绑定至子节点的属性上
     *
     */
    if (isVue2) {
      // 获取当前实例
      const instance = getCurrentInstance()?.proxy;
      popAttrs.value = {
        class: [
          instance?.$vnode.data?.staticClass,
          instance?.$vnode.data?.class,
        ].join(' '),
        style: {
          ...instance?.$vnode.data.staticStyle,
          ...instance?.$vnode.data.style,
        },
      };
    }

    // 监听visible
    watch(
      () => props.visible,
      () => {
        if (props.visible) {
          open();
        }
        if (!props.visible) {
          close();
        }
      },
    );

    /**
     * 点击遮罩层
     * @param e
     */
    const onClickOverlay = (e: Event) => {
      emit('clickOverlay', e);
      if (props.closeOnClickOverlay) {
        close();
      }
    };

    /**
     * 打开弹窗
     */
    const open = () => {
      if (!opened) {
        opened = true;
        zIndex.value = props.zIndex !== undefined ? +props.zIndex : initIndex.next();
        emit('update:visible', true);
        if (props.destroyOnClose) {
          showSlot.value = true;
        }
        emit('open');
      }
    };

    /**
     * 关闭弹窗
     */
    const close = () => {
      if (opened) {
        opened = false;
        emit('update:visible', false);
        emit('close');
      }
    };

    /**
     * 弹窗打开后
     * @param el
     */
    const onOpened = (el) => {
      emit('opened', el);
    };

    /**
     * 弹窗关闭后
     * @param el
     */
    const onClosed = (el) => {
      // 销毁弹窗
      if (props.destroyOnClose) {
        showSlot.value = false;
      }
      emit('closed', el);
    };

    return {
      n,
      isLiteapp,
      popAttrs,
      classes,
      popStyle,
      showSlot,
      normalizedZIndex,
      animationDuration,
      onOpened,
      onClosed,
      onClickOverlay,
    };
  },
});
</script>
